import dayjs, { type OpUnitType, type QUnitType } from "dayjs";
import type { RuleObject } from "antd/es/form";

const localFormatNumeric = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
});

const localFormatNumericWithTime = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "numeric",
    hour12: true,
    minute: "2-digit",
});

const localFormatNumericWithTimePlus = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "numeric",
    hour12: true,
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "long",
});

export const defaultDateFormatString = "MM/DD/YYYY";

// Format of date-only over the API; matches old formatDateForApi
export const apiFormatString = "YYYY-MM-DD";

/**
 * Format date as `mm/dd/yyyy` or `mm/dd/yyyy, h:mm a/p`, or `mm/dd/yyyy, h:mm:ss a/p time zone`
 * @param date The date to be rendered to text.
 * @param includeTime When true, time is included. When "long", seconds and time zone are included.
 * @returns String containing the date, with numeric parts, conforming to en-us culture.
 */
export function formatDateNumerically(date: number | Date, includeTime?: boolean | "long") {
    return (
        includeTime === true
            ? localFormatNumericWithTime
            : includeTime === "long"
              ? localFormatNumericWithTimePlus
              : localFormatNumeric
    ).format(date);
}

const rxApiDate = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Given a date string formatted as `yyyy-mm-dd`, provide date milliseconds that represent midnight local time, on that date
 * Given a date string formatted as `yyyy-MM-ddTHH:mm:ss.fffzzz`, provide date milliseconds that represent the point in time (UTC)
 * @param date Date string formatted as `yyyy-mm-dd` (perhaps a DateOnly fact from within an API response).
 * Alternatively, date string formatted as `yyyy-MM-ddTHH:mm:ss.fffzzz`.
 * @returns Date milliseconds that represent midnight local time on the provided date (or null if input was null),
 * or the point in time in UTC.
 */
export function parseApiDate(date: string | null) {
    if (!date) {
        return null;
    } else if (isApiDateWithTime(date)) {
        // input ex. "2022-02-18T18:25:18.450-04:00"
        return Date.parse(date);
    } else if (!rxApiDate.test(date)) {
        throw new Error(`incorrect parseApiDate format (${date})`);
    } else {
        // input ex. "2022-12-31"
        const fauxutc = new Date(Date.parse(date + "T00:00:00.000Z"));
        const year = fauxutc.getUTCFullYear();
        const month = fauxutc.getUTCMonth();
        const day = fauxutc.getUTCDate();
        const result = new Date(year, month, day, 0, 0, 0, 0);
        return result.getTime(); // Date milliseconds
    }
}

/**
 * Convert a date string formatted as `yyyy-mm-dd` to a string formatted as `mm/dd/yyyy`
 * @param date Date string formatted as `yyyy-mm-dd` (perhaps a DateOnly fact from within an API response). If null, returns null.
 * @returns  String formatted as `mm/dd/yyyy`
 */
export function apiDateToMDY(date: string | null) {
    if (!date) {
        return null;
    } else if (!rxApiDate.test(date)) {
        throw new Error(`incorrect apiDateToMDY format (${date})`);
    }
    const [year, month, day] = date.split("-");
    return `${month}/${day}/${year}`;
}

/**
 * Given a date string formatted as `yyyy-mm-dd`, return false
 * Given a date string potentially formatted as `yyyy-MM-ddTHH:mm:ss.fffzzz`, return true
 * @returns True if the date string provided is probably in `yyyy-MM-ddTHH:mm:ss.fffzzz` form
 */
export function isApiDateWithTime(date: string) {
    return date.length === 29;
}

/**
 * provide number of days between two dates
 * @param date1 The first date
 * @param date2 The second date
 * @returns Number of days between the two dates
 */
export function getDaysBetweenDates(date1: number | Date, date2: number | Date) {
    const date1Ms = typeof date1 === "number" ? date1 : date1.getTime();
    const date2Ms = typeof date2 === "number" ? date2 : date2.getTime();

    return Math.floor((date2Ms - date1Ms) / 86400000);
}

/**
 * Validate a date as an AntD form item rule
 * @param minDate The minimum date
 * @param maxDate The maximum date
 * @returns A validator function
 */
export function dateValidator({ minDate, maxDate }: { minDate?: dayjs.Dayjs; maxDate?: dayjs.Dayjs } = {}) {
    return (_rule: RuleObject, value: string) => {
        // if the value is blank or the default value, reject
        if (!value || value === defaultDateFormatString) {
            return Promise.reject(new Error("Date of Birth required."));
        }

        // current date
        const date = dayjs(value);

        // if the date is not valid, reject
        if (!date.isValid() || (minDate && date.isBefore(minDate)) || (maxDate && date.isAfter(maxDate))) {
            return Promise.reject(new Error("Date of Birth must be valid."));
        }

        // date is valid, resolve
        return Promise.resolve();
    };
}

/**
 * Get the difference between two dates in the specified unit of time.
 * @param date1 The first date
 * @param date2 The second date
 * @param type The unit of time to measure the difference in
 * @returns The difference between the two dates in the specified unit of time
 */
export function differenceInTimeUnits(date1: number | Date, date2: number | Date, type: QUnitType | OpUnitType) {
    return dayjs(date1).diff(dayjs(date2), type, false);
}
